import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';

interface UserInfo {
  name: string;
  email: string;
  picture: string;
}

// Use a simpler approach for environment variables
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';
const GOOGLE_CLIENT_SECRET = process.env.REACT_APP_GOOGLE_CLIENT_SECRET || '';
const GOOGLE_REDIRECT_URI = process.env.REACT_APP_GOOGLE_REDIRECT_URI || 'http://localhost:3000';

const Home: React.FC = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState<UserInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [loginError, setLoginError] = useState<string | null>(null);

  useEffect(() => {
    // Check if user is logged in
    const userStr = localStorage.getItem('user_info');
    if (userStr) {
      try {
        setUser(JSON.parse(userStr));
      } catch (error) {
        console.error('Error parsing user info:', error);
        localStorage.removeItem('user_info');
      }
    }
    
    // Check for OAuth callback
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const state = urlParams.get('state');
    const savedState = localStorage.getItem('oauth_state');
    
    if (code && state) {
      // Verify state to prevent CSRF attacks
      if (state === savedState) {
        handleOAuthCallback(code);
        // Clean up URL and state
        window.history.replaceState({}, document.title, window.location.pathname);
        localStorage.removeItem('oauth_state');
      } else {
        setLoginError('Invalid state parameter. Please try logging in again.');
        console.error('State mismatch, possible CSRF attack');
      }
    }
    
    setLoading(false);
  }, []);

  const handleOAuthCallback = async (code: string) => {
    try {
      setLoading(true);
      // Exchange code for tokens
      const tokenResponse = await fetch('https://oauth2.googleapis.com/token', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          code,
          client_id: GOOGLE_CLIENT_ID,
          client_secret: GOOGLE_CLIENT_SECRET,
          redirect_uri: GOOGLE_REDIRECT_URI,
          grant_type: 'authorization_code',
        }),
      });

      if (!tokenResponse.ok) {
        const errorData = await tokenResponse.json();
        throw new Error(`Token exchange failed: ${errorData.error_description || 'Unknown error'}`);
      }

      const tokenData = await tokenResponse.json();
      
      if (tokenData.access_token) {
        // Get user info with the access token
        const userInfoResponse = await fetch('https://www.googleapis.com/oauth2/v2/userinfo', {
          headers: {
            Authorization: `Bearer ${tokenData.access_token}`,
          },
        });
        
        if (!userInfoResponse.ok) {
          throw new Error('Failed to fetch user info');
        }
        
        const userData = await userInfoResponse.json();
        
        const userInfo: UserInfo = {
          name: userData.name,
          email: userData.email,
          picture: userData.picture,
        };
        
        setUser(userInfo);
        localStorage.setItem('user_info', JSON.stringify(userInfo));
        console.log('User logged in successfully:', userInfo);
      } else {
        throw new Error('No access token received');
      }
    } catch (error) {
      console.error('OAuth callback error:', error);
      setLoginError(error instanceof Error ? error.message : 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    try {
      // Generate a random state value for security
      const state = Math.random().toString(36).substring(2, 15);
      localStorage.setItem('oauth_state', state);
      
      // Build the Google OAuth URL
      const authUrl = new URL('https://accounts.google.com/o/oauth2/v2/auth');
      authUrl.searchParams.append('client_id', GOOGLE_CLIENT_ID);
      authUrl.searchParams.append('redirect_uri', GOOGLE_REDIRECT_URI);
      authUrl.searchParams.append('response_type', 'code');
      authUrl.searchParams.append('scope', 'email profile');
      authUrl.searchParams.append('state', state);
      authUrl.searchParams.append('access_type', 'offline');
      authUrl.searchParams.append('prompt', 'consent');
      
      // Redirect to Google's OAuth page
      window.location.href = authUrl.toString();
    } catch (error) {
      console.error('Login error:', error);
      setLoginError('Failed to initiate login. Please try again.');
    }
  };

  const handleLogout = () => {
    setUser(null);
    localStorage.removeItem('user_info');
    console.log('User logged out');
  };

  const handleStartLearning = () => {
    navigate('/topics');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-white flex flex-col items-center gap-4">
          <div className="w-8 h-8 border-4 border-white/20 border-t-white rounded-full animate-spin"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white relative overflow-hidden flex flex-col items-center justify-center">
      {/* Minimal grid background */}
      <div className="fixed inset-0 bg-[linear-gradient(rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[length:64px_64px] [background-position-x:center]"></div>
      
      {/* Content */}
      <div className="relative z-10 w-full max-w-5xl px-4 flex flex-col items-center">
        <motion.div
          className="text-center mb-20"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
        >
          <h1 
            className="text-9xl tracking-tight select-none font-normal" 
        style={{ fontFamily: '"Space Grotesk", system-ui' }}
      >
            <span className="font-normal">ALGO</span>
            <span className="font-normal ml-6">FLOW</span>
      </h1>
          <motion.p 
            className="text-white/60 mt-4 text-lg"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4, duration: 0.5 }}
          >
            Master algorithms and data structures with interactive learning
          </motion.p>
        </motion.div>

        {loginError && (
          <motion.div 
            className="w-full max-w-md mb-6 p-4 bg-white/5 border border-white/10 rounded-lg text-white text-center"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <p>{loginError}</p>
        <button
              onClick={() => setLoginError(null)}
              className="mt-2 text-sm text-white/70 hover:text-white"
        >
              Dismiss
        </button>
          </motion.div>
        )}

        <div className="flex flex-col items-center gap-8 w-full max-w-md">
          {user ? (
            <>
              {/* User Profile */}
              <motion.div 
                className="w-full bg-white/5 border border-white/10 rounded-lg p-8 text-center shadow-sm"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2, duration: 0.5 }}
                whileHover={{ y: -5 }}
              >
                <div className="flex flex-col items-center gap-5">
                  <div className="relative">
                    <img
                      src={user.picture || `https://api.dicebear.com/7.x/bottts/svg?seed=${user.email}&backgroundColor=b6e3f4,c0aede,d1f4d9,ffdfbf,ffd5dc`}
                      alt={user.name}
                      className="w-20 h-20 rounded-full border border-white/10 shadow-sm"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.src = `https://api.dicebear.com/7.x/bottts/svg?seed=${user.email}&backgroundColor=b6e3f4,c0aede,d1f4d9,ffdfbf,ffd5dc`;
                      }}
                    />
                  </div>
                  <div>
                    <h2 className="text-2xl font-normal text-white mb-1">{user.name}</h2>
                    <p className="text-white/60 text-sm">{user.email}</p>
                  </div>
                </div>
              </motion.div>

              {/* Start Learning Button */}
              <motion.button
                onClick={handleStartLearning}
                className="w-full px-8 py-4 bg-white text-black rounded-lg transition-all duration-300 flex items-center justify-center gap-3 shadow-sm hover:bg-white/90"
                whileHover={{ scale: 1.02, y: -2 }}
                whileTap={{ scale: 0.98 }}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3, duration: 0.5 }}
              >
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                <span className="text-lg font-medium">Start Learning</span>
              </motion.button>

              {/* Logout Button */}
              <motion.button
                onClick={handleLogout}
                className="w-full px-8 py-4 bg-transparent border border-white/10 text-white rounded-lg transition-all duration-300 flex items-center justify-center gap-3 shadow-sm hover:bg-white/5"
                whileHover={{ scale: 1.02, y: -2 }}
                whileTap={{ scale: 0.98 }}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4, duration: 0.5 }}
              >
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
                <span className="text-lg font-medium">Logout</span>
              </motion.button>
            </>
          ) : (
            <motion.button
              onClick={handleGoogleLogin}
              className="w-full px-8 py-4 bg-transparent border border-white/10 text-white rounded-lg transition-all duration-300 flex items-center justify-center gap-3 shadow-sm hover:bg-white/5"
              whileHover={{ scale: 1.02, y: -2 }}
              whileTap={{ scale: 0.98 }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.5 }}
            >
              <svg className="w-6 h-6" viewBox="0 0 24 24">
                <path
                  fill="currentColor"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="currentColor"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="currentColor"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="currentColor"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
              <span className="text-lg font-normal">Login with Google</span>
            </motion.button>
          )}
        </div>
      </div>
    </div>
  );
};

export default Home;
